import {
  Close,
  Delete,
  Upload,
  Business,
  TuneOutlined,
  CreditCard,
  Lock,
  Group,
  PersonAdd,
  Public,
  Search,
} from "@mui/icons-material";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  IconButton,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Pagination,
  Select,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import axios from "axios";
import { useEffect, useState, useRef } from "react";
import LoadingButton from "../../../components/LoadingButton";
import { COMPANY_SIZES, INDUSTRIES } from "../../../constants";
import {
  FeatureName,
  SELECTABLE_FEATURES,
  getFeatureDisplayName,
  FEATURES,
} from "../../../constants/features";
import { hostname } from "../../../utils";
import { buildCompanyDescriptionPrompt } from "../../../utils/companyPrompts";
import { useEmployeeTerminology } from "../../../utils/terminology";

// Features that require feature flag access
const FEATURE_FLAG_FEATURES: FeatureName[] = [FEATURES.AUDIENCE_BUILDER];

interface OrganizationOption {
  id: string;
  name: string;
}

interface CompanyData {
  id?: string;
  name: string;
  description?: string;
  industry?: string;
  size?: string;
  location?: string;
  website?: string;
  logoUrl?: string;
  allowedFeatures?: string[] | null;
  features?: Record<string, string> | null; // Organization's features
  creditLimit?: number | null;
  creditUsage?: number;
  zeroDataRetention?: boolean;
  mfaRequired?: boolean;
  ownerEmail?: string;
  ownerFirstName?: string;
  ownerLastName?: string;
  organization_id?: string | null;
}

interface CompanyFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  getToken: () => Promise<string>;
  whitelabelId: string;
  editCompany?: CompanyData | null;
  organizations?: OrganizationOption[];
}

interface CompanyEmployee {
  id: string;
  name: string;
  title: string;
  picture: string;
  tags: string[];
  public: boolean;
  description?: string;
  isPublicEmployee: boolean;
}

interface PublicEmployee {
  id: string;
  name: string;
  title: string;
  description: string;
  picture: string;
  tags: string[];
  ownerCompany: {
    id: string;
    name: string;
    logoUrl?: string;
  };
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      style={{
        flex: 1,
        overflow: "auto",
        display: value === index ? "block" : "none",
      }}
      {...other}
    >
      {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
    </div>
  );
}

export default function CompanyFormDialog({
  open,
  onClose,
  onSaved,
  getToken,
  whitelabelId,
  editCompany,
  organizations = [],
}: CompanyFormDialogProps) {
  const isEdit = !!editCompany?.id;
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { employees } = useEmployeeTerminology();

  // Tab state
  const [currentTab, setCurrentTab] = useState(0);

  // Basic fields
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [industry, setIndustry] = useState("");
  const [size, setSize] = useState("");
  const [location, setLocation] = useState("");
  const [website, setWebsite] = useState("");
  const [logoUrl, setLogoUrl] = useState("");
  const [organizationId, setOrganizationId] = useState<string>("");

  // Owner fields (create only)
  const [ownerEmail, setOwnerEmail] = useState("");
  const [ownerFirstName, setOwnerFirstName] = useState("");
  const [ownerLastName, setOwnerLastName] = useState("");

  // Features state
  const [selectedFeatures, setSelectedFeatures] = useState<string[]>([]);
  const [selectAll, setSelectAll] = useState(false);

  // Credit Quota state
  const [quotaEnabled, setQuotaEnabled] = useState(false);
  const [creditLimit, setCreditLimit] = useState<string>("");
  const [currentUsage, setCurrentUsage] = useState<number>(0);

  // Security state
  const [zeroDataRetention, setZeroDataRetention] = useState(false);
  const [mfaRequired, setMfaRequired] = useState(false);

  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [descLoading, setDescLoading] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  // Employee generation state
  const [generatingEmployees, setGeneratingEmployees] = useState(false);
  const [employeeCount, setEmployeeCount] = useState("3");
  const [employeeSuccess, setEmployeeSuccess] = useState("");

  // Employee list state
  const [companyEmployees, setCompanyEmployees] = useState<CompanyEmployee[]>(
    []
  );
  const [employeesLoading, setEmployeesLoading] = useState(false);

  // Public employee browse state
  const [showPublicBrowse, setShowPublicBrowse] = useState(false);
  const [publicEmployees, setPublicEmployees] = useState<PublicEmployee[]>([]);
  const [publicEmployeesLoading, setPublicEmployeesLoading] = useState(false);
  const [publicSearch, setPublicSearch] = useState("");
  const [publicPage, setPublicPage] = useState(1);
  const [publicTotal, setPublicTotal] = useState(0);
  const [addingEmployeeId, setAddingEmployeeId] = useState<string | null>(null);
  const publicPerPage = 6;

  // Feature flag state
  const [enabledFeatureFlags, setEnabledFeatureFlags] = useState<string[]>([]);

  // Fetch enabled feature flags for this whitelabel
  useEffect(() => {
    const fetchEnabledFeatureFlags = async () => {
      try {
        const token = await getToken();
        const response = await axios.get(`${hostname}/feature-flags/enabled`, {
          params: { whitelabelId },
          headers: { Authorization: `Bearer ${token}` },
        });
        setEnabledFeatureFlags(response.data.features || []);
      } catch (err) {
        console.error("Error fetching enabled feature flags:", err);
        setEnabledFeatureFlags([]);
      }
    };

    if (open && whitelabelId) {
      fetchEnabledFeatureFlags();
    }
  }, [open, whitelabelId, getToken]);

  // Compute available features based on organization's enabled features + feature flags
  const getAvailableFeatures = (): string[] => {
    // Base selectable features + any enabled feature flag features
    const baseFeatures = [
      ...SELECTABLE_FEATURES,
      ...FEATURE_FLAG_FEATURES.filter((f) => enabledFeatureFlags.includes(f)),
    ];

    if (!editCompany?.features) {
      // No organization or no org features - show all base features
      return baseFeatures;
    }
    // Filter to only features that are "enabled" in the org
    return baseFeatures.filter(
      (feature) => editCompany.features?.[feature] === "enabled"
    );
  };

  const displayFeatures = getAvailableFeatures();

  useEffect(() => {
    if (open) {
      const availableFeatures = getAvailableFeatures();

      if (editCompany) {
        setName(editCompany.name || "");
        setDescription(editCompany.description || "");
        setIndustry(editCompany.industry || "");
        setSize(editCompany.size || "");
        setLocation(editCompany.location || "");
        setWebsite(editCompany.website || "");
        setLogoUrl(editCompany.logoUrl || "");
        setOrganizationId(editCompany.organization_id || "");

        // Features - filter by both what company has and what org allows
        // For existing companies, exclude feature flag features from defaults
        // unless they were explicitly saved
        let features: string[];
        if (
          editCompany.allowedFeatures !== null &&
          editCompany.allowedFeatures !== undefined
        ) {
          // Company has explicit feature list - filter to available
          features = editCompany.allowedFeatures.filter((f: string) =>
            availableFeatures.includes(f)
          );
        } else {
          // No restrictions (null) - use all available except feature flag features
          // that weren't previously enabled
          features = availableFeatures.filter(
            (f) => !FEATURE_FLAG_FEATURES.includes(f as FeatureName)
          );
        }
        setSelectedFeatures(features);
        setSelectAll(features.length === availableFeatures.length);

        // Credit quota
        const hasQuota =
          editCompany.creditLimit !== null &&
          editCompany.creditLimit !== undefined;
        setQuotaEnabled(hasQuota);
        setCreditLimit(hasQuota ? String(editCompany.creditLimit) : "");
        setCurrentUsage(editCompany.creditUsage || 0);

        // Security
        setZeroDataRetention(editCompany.zeroDataRetention || false);
        setMfaRequired(editCompany.mfaRequired || false);
      } else {
        // Reset all fields for new company
        setName("");
        setDescription("");
        setIndustry("");
        setSize("");
        setLocation("");
        setWebsite("");
        setLogoUrl("");
        setOrganizationId("");
        setOwnerEmail("");
        setOwnerFirstName("");
        setOwnerLastName("");
        setSelectedFeatures(displayFeatures);
        setSelectAll(true);
        setQuotaEnabled(false);
        setCreditLimit("");
        setCurrentUsage(0);
        setZeroDataRetention(false);
        setMfaRequired(false);
        setEmployeeCount("3");
      }
      setCurrentTab(0);
      setError("");
      setUploadError(null);
      setEmployeeSuccess("");
      setCompanyEmployees([]);
      setShowPublicBrowse(false);
      setPublicEmployees([]);
      setPublicSearch("");
      setPublicPage(1);
      setPublicTotal(0);
    }
  }, [open, editCompany]);

  // Features handlers
  const handleFeatureToggle = (feature: string) => {
    setSelectedFeatures((prev) => {
      if (prev.includes(feature)) {
        const newFeatures = prev.filter((f) => f !== feature);
        setSelectAll(false);
        return newFeatures;
      } else {
        const newFeatures = [...prev, feature];
        setSelectAll(newFeatures.length === displayFeatures.length);
        return newFeatures;
      }
    });
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedFeatures([]);
      setSelectAll(false);
    } else {
      setSelectedFeatures(displayFeatures);
      setSelectAll(true);
    }
  };

  const generateDescription = async () => {
    if (!name) {
      setError("Please enter a company name first");
      return;
    }

    setDescLoading(true);
    setError("");

    try {
      const token = await getToken();
      const prompt = buildCompanyDescriptionPrompt({
        name,
        industry,
        website,
        location,
        size,
        maxWords: 400,
      });

      const response = await axios.post(
        `${hostname}/completions/messages`,
        {
          tools: true,
          messages: [{ role: "user", content: prompt }],
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data?.content) {
        setDescription(response.data.content.trim());
      }
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to generate description");
    } finally {
      setDescLoading(false);
    }
  };

  const handleLogoUploadClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const file = files[0];

    if (!file.type.startsWith("image/")) {
      setUploadError("Please upload an image file (PNG, JPG, etc.)");
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      setUploadError("Image size should be less than 2MB");
      return;
    }

    setUploadingLogo(true);
    setUploadError(null);

    try {
      const token = await getToken();

      const reader = new FileReader();
      reader.onloadend = async () => {
        try {
          const base64String = reader.result as string;

          const img = new Image();
          img.onload = async () => {
            const imageData = {
              name: `${name || "Company"} Logo`,
              category: "company-logo",
              url: base64String,
              mimeType: file.type,
              width: img.width.toString(),
              height: img.height.toString(),
              fileSize: file.size.toString(),
              public: true,
            };

            const uploadResponse = await axios.post(
              `${hostname}/platform-images`,
              imageData,
              {
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${token}`,
                },
              }
            );

            if (uploadResponse.status === 201) {
              const responseData = uploadResponse.data;
              const publicUrl = responseData.publicUrl || responseData.url;
              setLogoUrl(publicUrl);
              setUploadingLogo(false);
            }
          };
          img.src = base64String;
        } catch (err) {
          console.error("Error uploading logo:", err);
          setUploadError("Failed to upload image. Please try again.");
          setUploadingLogo(false);
        }
      };

      reader.readAsDataURL(file);
    } catch (err) {
      console.error("Error reading file:", err);
      setUploadError("Failed to read image file. Please try again.");
      setUploadingLogo(false);
    } finally {
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleRemoveLogo = () => {
    setLogoUrl("");
    setUploadError(null);
  };

  const handleGenerateEmployees = async () => {
    if (!editCompany?.id) return;

    setGeneratingEmployees(true);
    setError("");
    setEmployeeSuccess("");

    try {
      const token = await getToken();
      const response = await axios.post(
        `${hostname}/whitelabel/${whitelabelId}/companies/${editCompany.id}/generate-employees`,
        { numEmployees: parseInt(employeeCount) || 3 },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data?.success) {
        const count = response.data.count || 0;
        setEmployeeSuccess(
          `Successfully generated ${count} AI employee${count === 1 ? "" : "s"}`
        );
      }
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to generate employees");
    } finally {
      setGeneratingEmployees(false);
    }
  };

  const fetchCompanyEmployees = async () => {
    if (!editCompany?.id) return;
    setEmployeesLoading(true);
    try {
      const token = await getToken();
      const response = await axios.get(
        `${hostname}/whitelabel/${whitelabelId}/companies/${editCompany.id}/employees`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCompanyEmployees(response.data.employees || []);
    } catch (err) {
      console.error("Error fetching employees:", err);
    } finally {
      setEmployeesLoading(false);
    }
  };

  const fetchPublicEmployees = async () => {
    if (!editCompany?.id) return;
    setPublicEmployeesLoading(true);
    try {
      const token = await getToken();
      const response = await axios.get(
        `${hostname}/whitelabel/${whitelabelId}/companies/${editCompany.id}/public-employees`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            page: publicPage,
            per_page: publicPerPage,
            search: publicSearch.trim() || undefined,
          },
        }
      );
      setPublicEmployees(response.data.employees || []);
      setPublicTotal(response.data.total || 0);
    } catch (err) {
      console.error("Error fetching public employees:", err);
    } finally {
      setPublicEmployeesLoading(false);
    }
  };

  const handleAddPublicEmployee = async (publicEmployeeId: string) => {
    if (!editCompany?.id) return;
    setAddingEmployeeId(publicEmployeeId);
    try {
      const token = await getToken();
      await axios.post(
        `${hostname}/whitelabel/${whitelabelId}/companies/${editCompany.id}/public-employees`,
        { publicEmployeeId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Refresh company employees and go back to employees view
      await fetchCompanyEmployees();
      setShowPublicBrowse(false);
    } catch (err: any) {
      setError(
        err.response?.data?.error || "Failed to add public employee"
      );
    } finally {
      setAddingEmployeeId(null);
    }
  };

  // Fetch employees when tab switches to employees tab
  useEffect(() => {
    if (open && isEdit && currentTab === 4) {
      fetchCompanyEmployees();
    }
  }, [open, currentTab, editCompany?.id]);

  // Fetch public employees when browse is opened or search/page changes
  useEffect(() => {
    if (showPublicBrowse) {
      fetchPublicEmployees();
    }
  }, [showPublicBrowse, publicPage, publicSearch]);

  const handleSave = async () => {
    if (!name.trim()) {
      setError("Company name is required");
      setCurrentTab(0);
      return;
    }

    // Validate credit limit if quota is enabled
    if (quotaEnabled) {
      const limit = parseFloat(creditLimit);
      if (isNaN(limit) || limit < 0) {
        setError("Please enter a valid credit limit (0 or greater)");
        setCurrentTab(2);
        return;
      }
    }

    setLoading(true);
    setError("");

    try {
      const token = await getToken();

      // Build features array
      let featuresToSave: string[] | null;
      if (selectedFeatures.length === displayFeatures.length) {
        featuresToSave = null; // No restrictions
      } else {
        featuresToSave = [
          ...selectedFeatures,
          FEATURES.COLLABORATORS,
          FEATURES.INTEGRATIONS,
        ];
      }

      const payload: any = {
        name: name.trim(),
        description: description.trim() || undefined,
        industry: industry || undefined,
        size: size || undefined,
        location: location.trim() || undefined,
        website: website.trim() || undefined,
        logoUrl: logoUrl || undefined,
        allowedFeatures: featuresToSave,
        creditLimit: quotaEnabled ? parseFloat(creditLimit) : null,
        zeroDataRetention,
        mfaRequired,
        organizationId: organizationId || null,
      };

      if (isEdit) {
        await axios.put(
          `${hostname}/whitelabel/${whitelabelId}/companies/${editCompany!.id}`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        if (ownerEmail) {
          payload.ownerEmail = ownerEmail.trim();
          payload.ownerFirstName = ownerFirstName.trim();
          payload.ownerLastName = ownerLastName.trim();
        }
        await axios.post(
          `${hostname}/whitelabel/${whitelabelId}/companies`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }

      onSaved();
      onClose();
    } catch (err: any) {
      const msg = err.response?.data?.error || "Failed to save company";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const usagePercentage =
    quotaEnabled && creditLimit
      ? Math.min((currentUsage / parseFloat(creditLimit)) * 100, 100)
      : 0;

  const renderDetailsTab = () => (
    <>
      <Typography variant="subtitle2" fontWeight="bold">
        Company Details
      </Typography>
      <TextField
        label="Company Name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        fullWidth
        required
        size="small"
        sx={{ mt: 2 }}
      />

      <Grid container spacing={2} sx={{ mt: 0 }}>
        <Grid item xs={6}>
          <TextField
            select
            label="Industry"
            value={industry}
            onChange={(e) => setIndustry(e.target.value)}
            fullWidth
            size="small"
          >
            <MenuItem value="">
              <em>Select Industry</em>
            </MenuItem>
            {INDUSTRIES.map((ind) => (
              <MenuItem key={ind} value={ind}>
                {ind}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={6}>
          <TextField
            select
            label="Company Size"
            value={size}
            onChange={(e) => setSize(e.target.value)}
            fullWidth
            size="small"
          >
            <MenuItem value="">
              <em>Select Size</em>
            </MenuItem>
            {COMPANY_SIZES.map((s) => (
              <MenuItem key={s} value={s}>
                {s}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mt: 0 }}>
        <Grid item xs={6}>
          <TextField
            label="Website"
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
            fullWidth
            size="small"
            placeholder="https://www.example.com"
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            label="Location"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            fullWidth
            size="small"
            placeholder="City, Country"
          />
        </Grid>
      </Grid>

      <Box sx={{ mt: 2 }}>
        <TextField
          label="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          fullWidth
          multiline
          rows={4}
          size="small"
        />
        <LoadingButton
          loading={descLoading}
          size="small"
          onClick={generateDescription}
          sx={{ mt: 1 }}
        >
          Generate Description
        </LoadingButton>
      </Box>

      {/* Logo Upload */}
      <Box sx={{ mt: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Company Logo (Optional)
        </Typography>
        <Card variant="outlined" sx={{ p: 2 }}>
          {logoUrl ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <img
                src={/^https?:\/\/[a-zA-Z0-9]/.test(logoUrl) ? logoUrl : ""}
                alt="Company logo"
                style={{
                  maxHeight: 60,
                  maxWidth: 120,
                  objectFit: "contain",
                }}
              />
              <Box sx={{ flexGrow: 1 }} />
              <IconButton
                onClick={handleRemoveLogo}
                disabled={uploadingLogo}
                size="small"
                color="error"
              >
                <Delete />
              </IconButton>
            </Box>
          ) : (
            <Box sx={{ textAlign: "center", py: 2 }}>
              {uploadingLogo ? (
                <CircularProgress size={24} />
              ) : (
                <>
                  <Button
                    startIcon={<Upload />}
                    onClick={handleLogoUploadClick}
                    variant="outlined"
                    disabled={uploadingLogo}
                    size="small"
                  >
                    Upload Logo
                  </Button>
                  <Typography
                    variant="caption"
                    display="block"
                    sx={{ mt: 1 }}
                    color="text.secondary"
                  >
                    PNG, JPG up to 2MB - Suggested size: 200x200
                  </Typography>
                </>
              )}
            </Box>
          )}
          {uploadError && (
            <Typography
              variant="caption"
              color="error"
              sx={{ mt: 1, display: "block" }}
            >
              {uploadError}
            </Typography>
          )}
        </Card>
      </Box>

      <input
        type="file"
        accept="image/*"
        ref={fileInputRef}
        style={{ display: "none" }}
        onChange={handleFileChange}
      />

      {/* Organization */}
      {organizations.length > 0 && (
        <FormControl fullWidth size="small" sx={{ mt: 2 }}>
          <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
            Organization
          </Typography>
          <Select
            value={organizationId}
            onChange={(e) => setOrganizationId(e.target.value)}
            displayEmpty
          >
            <MenuItem value="">
              <em>None</em>
            </MenuItem>
            {organizations.map((org) => (
              <MenuItem key={org.id} value={org.id}>
                {org.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {/* Owner (create only) */}
      {!isEdit && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" fontWeight="bold">
            Owner (Optional)
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Provision a user as the company owner. Leave blank to create without
            an owner.
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <TextField
                label="Email"
                value={ownerEmail}
                onChange={(e) => setOwnerEmail(e.target.value)}
                fullWidth
                size="small"
                type="email"
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                label="First Name"
                value={ownerFirstName}
                onChange={(e) => setOwnerFirstName(e.target.value)}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                label="Last Name"
                value={ownerLastName}
                onChange={(e) => setOwnerLastName(e.target.value)}
                fullWidth
                size="small"
              />
            </Grid>
          </Grid>
        </Box>
      )}
    </>
  );

  const renderFeaturesTab = () => {
    const hasOrgRestrictions =
      editCompany?.features &&
      displayFeatures.length < SELECTABLE_FEATURES.length;

    return (
      <>
        <Typography variant="body2" sx={{ mb: 2 }}>
          Select which features are available for this company. These
          restrictions will apply to all users in the company.
        </Typography>

        {hasOrgRestrictions && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Only showing features enabled by the organization. To enable more
            features, update the organization settings.
          </Alert>
        )}

        <FormControlLabel
          control={<Checkbox checked={selectAll} onChange={handleSelectAll} />}
          label={<strong>Select All (No Restrictions)</strong>}
          sx={{ mb: 1 }}
        />

        <FormGroup>
          {displayFeatures.map((feature) => (
            <FormControlLabel
              key={feature}
              control={
                <Checkbox
                  checked={selectedFeatures.includes(feature)}
                  onChange={() => handleFeatureToggle(feature)}
                />
              }
              label={getFeatureDisplayName(feature as FeatureName, employees)}
            />
          ))}
        </FormGroup>

        <Box sx={{ mt: 2, p: 2, bgcolor: "action.hover", borderRadius: 1 }}>
          <Typography variant="caption" color="text.secondary">
            <strong>Note:</strong> If no features are selected, all features
            will be restricted. Collaborators and Integrations are always
            enabled for company management.
          </Typography>
        </Box>
      </>
    );
  };

  const renderCreditQuotaTab = () => (
    <>
      <Typography variant="body2" sx={{ mb: 2 }}>
        Set a monthly credit limit for this company. When the limit is reached,
        operations will be blocked until the next month or the quota is
        increased.
      </Typography>

      <FormControlLabel
        control={
          <Switch
            checked={quotaEnabled}
            onChange={(e) => setQuotaEnabled(e.target.checked)}
          />
        }
        label="Enable credit quota for this company"
        sx={{ mb: 2 }}
      />

      {quotaEnabled && (
        <>
          <TextField
            label="Monthly Credit Limit"
            type="number"
            value={creditLimit}
            onChange={(e) => setCreditLimit(e.target.value)}
            fullWidth
            sx={{ mb: 2 }}
            inputProps={{ min: 0 }}
            helperText="Set the maximum credits this company can use per month"
          />

          {isEdit && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Current Month Usage: {currentUsage.toFixed(1)} /{" "}
                {creditLimit || "∞"} credits
              </Typography>
              <LinearProgress
                variant="determinate"
                value={usagePercentage}
                color={
                  usagePercentage >= 90
                    ? "error"
                    : usagePercentage >= 70
                    ? "warning"
                    : "primary"
                }
                sx={{ height: 8, borderRadius: 4 }}
              />
              <Typography
                variant="caption"
                color={usagePercentage >= 90 ? "error" : "text.secondary"}
              >
                {usagePercentage.toFixed(1)}% used
              </Typography>
            </Box>
          )}
        </>
      )}

      <Box sx={{ mt: 2, p: 2, bgcolor: "action.hover", borderRadius: 1 }}>
        <Typography variant="caption" color="text.secondary">
          <strong>Note:</strong> When disabled, this company will have no credit
          limit and can use unlimited credits.
        </Typography>
      </Box>
    </>
  );

  const renderSecurityTab = () => (
    <>
      <Typography variant="body2" sx={{ mb: 2 }}>
        Configure security and privacy settings for this company.
      </Typography>

      <Alert severity="info" sx={{ mb: 3 }}>
        By default, none of our AI providers can use your data for training.
      </Alert>

      <Box
        sx={{
          p: 2,
          border: 1,
          borderColor: "divider",
          borderRadius: 1,
          mb: 2,
        }}
      >
        <FormControlLabel
          control={
            <Switch
              checked={zeroDataRetention}
              onChange={(e) => setZeroDataRetention(e.target.checked)}
            />
          }
          label={
            <Box>
              <Typography variant="subtitle2">
                Zero Data Retention (ZDR)
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Only use AI providers that do not retain your data
              </Typography>
            </Box>
          }
          sx={{ m: 0, alignItems: "flex-start" }}
        />
      </Box>

      <Box sx={{ p: 2, bgcolor: "action.hover", borderRadius: 1, mb: 3 }}>
        <Typography variant="caption" color="text.secondary">
          <strong>About Zero Data Retention:</strong> When enabled, all AI
          requests for this company will only be routed to providers with zero
          data retention policies. This ensures prompts and responses are not
          stored or retained by the provider. Note that this may limit which AI
          models are available.
        </Typography>
      </Box>

      <Box
        sx={{
          p: 2,
          border: 1,
          borderColor: "divider",
          borderRadius: 1,
          mb: 2,
        }}
      >
        <FormControlLabel
          control={
            <Switch
              checked={mfaRequired}
              onChange={(e) => setMfaRequired(e.target.checked)}
            />
          }
          label={
            <Box>
              <Typography variant="subtitle2">
                Require Multi-Factor Authentication (MFA)
              </Typography>
              <Typography variant="body2" color="text.secondary">
                All users in this company must set up and verify MFA to access
                the platform
              </Typography>
            </Box>
          }
          sx={{ m: 0, alignItems: "flex-start" }}
        />
      </Box>

      <Box sx={{ p: 2, bgcolor: "action.hover", borderRadius: 1 }}>
        <Typography variant="caption" color="text.secondary">
          <strong>About MFA:</strong> When enabled, all users who are members of
          this company will be required to set up an authenticator app and enter
          a verification code each time they log in.
        </Typography>
      </Box>
    </>
  );

  const renderEmployeesTab = () => {
    if (showPublicBrowse) {
      const totalPages = Math.ceil(publicTotal / publicPerPage);

      return (
        <>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Public color="primary" />
              <Typography variant="subtitle2" fontWeight="bold">
                Add Public {employees}
              </Typography>
            </Box>
            <Button size="small" onClick={() => setShowPublicBrowse(false)}>
              Back to {employees}
            </Button>
          </Box>

          <TextField
            fullWidth
            size="small"
            placeholder={`Search public ${employees.toLowerCase()} by name or title...`}
            value={publicSearch}
            onChange={(e) => {
              setPublicSearch(e.target.value);
              setPublicPage(1);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          />

          {publicEmployeesLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={24} />
            </Box>
          ) : publicEmployees.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Typography variant="body2" color="text.secondary">
                {publicSearch
                  ? `No public ${employees.toLowerCase()} found matching your search`
                  : `No public ${employees.toLowerCase()} available`}
              </Typography>
            </Box>
          ) : (
            <>
              <Grid container spacing={2}>
                {publicEmployees.map((emp) => (
                  <Grid item xs={12} md={6} key={emp.id}>
                    <Card variant="outlined" sx={{ height: "100%" }}>
                      <CardContent sx={{ pb: 0 }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            mb: 1,
                          }}
                        >
                          <Avatar
                            src={emp.picture}
                            sx={{ width: 40, height: 40, mr: 1.5 }}
                          />
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography
                              variant="body2"
                              fontWeight="bold"
                              noWrap
                            >
                              {emp.name}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              noWrap
                              display="block"
                            >
                              {emp.title}
                            </Typography>
                          </Box>
                        </Box>
                        {emp.description && (
                          <Tooltip title={emp.description} arrow enterDelay={500}>
                            <Typography
                              variant="caption"
                              sx={{
                                display: "-webkit-box",
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden",
                                mb: 1,
                              }}
                            >
                              {emp.description}
                            </Typography>
                          </Tooltip>
                        )}
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          <Business
                            sx={{ fontSize: 14, color: "text.secondary" }}
                          />
                          <Typography variant="caption" color="text.secondary">
                            {emp.ownerCompany?.name}
                          </Typography>
                        </Box>
                      </CardContent>
                      <CardActions>
                        <LoadingButton
                          size="small"
                          variant="contained"
                          startIcon={<PersonAdd />}
                          loading={addingEmployeeId === emp.id}
                          onClick={() => handleAddPublicEmployee(emp.id)}
                          fullWidth
                        >
                          Add to Company
                        </LoadingButton>
                      </CardActions>
                    </Card>
                  </Grid>
                ))}
              </Grid>

              {totalPages > 1 && (
                <Box
                  sx={{ display: "flex", justifyContent: "center", mt: 2 }}
                >
                  <Pagination
                    count={totalPages}
                    page={publicPage}
                    onChange={(_, value) => setPublicPage(value)}
                    color="primary"
                    size="small"
                  />
                </Box>
              )}
            </>
          )}
        </>
      );
    }

    return (
      <>
        {/* Employee List */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 2,
          }}
        >
          <Typography variant="subtitle2" fontWeight="bold">
            {employees} ({companyEmployees.length})
          </Typography>
          <Button
            size="small"
            variant="outlined"
            startIcon={<Public />}
            onClick={() => setShowPublicBrowse(true)}
          >
            Add Public {employees}
          </Button>
        </Box>

        {employeesLoading ? (
          <LinearProgress sx={{ mb: 2 }} />
        ) : companyEmployees.length === 0 ? (
          <Box
            sx={{
              textAlign: "center",
              py: 3,
              border: 1,
              borderColor: "divider",
              borderRadius: 1,
              mb: 2,
            }}
          >
            <Group
              sx={{ fontSize: 40, color: "text.secondary", mb: 1 }}
            />
            <Typography variant="body2" color="text.secondary">
              No {employees.toLowerCase()} yet. Generate some below or add
              public {employees.toLowerCase()}.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ mb: 2 }}>
            {companyEmployees.map((emp) => (
              <Box
                key={emp.id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  py: 1,
                  px: 1.5,
                  borderBottom: 1,
                  borderColor: "divider",
                  "&:last-child": { borderBottom: 0 },
                }}
              >
                <Avatar
                  src={emp.picture}
                  sx={{ width: 36, height: 36 }}
                />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                    }}
                  >
                    <Typography variant="body2" fontWeight="medium" noWrap>
                      {emp.name}
                    </Typography>
                    {emp.public && !emp.isPublicEmployee && (
                      <Chip
                        size="small"
                        icon={<Public />}
                        label="Public"
                        color="primary"
                        variant="outlined"
                        sx={{ height: 18, fontSize: "0.6rem" }}
                      />
                    )}
                    {emp.isPublicEmployee && (
                      <Chip
                        size="small"
                        icon={<Business />}
                        label="External"
                        color="secondary"
                        variant="outlined"
                        sx={{ height: 18, fontSize: "0.6rem" }}
                      />
                    )}
                  </Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    noWrap
                    display="block"
                  >
                    {emp.title}
                  </Typography>
                </Box>
                {emp.tags && emp.tags.length > 0 && (
                  <Stack
                    direction="row"
                    spacing={0.5}
                    sx={{ flexShrink: 0 }}
                  >
                    {emp.tags.slice(0, 2).map((tag) => (
                      <Chip
                        key={tag}
                        size="small"
                        label={tag}
                        sx={{ height: 18, fontSize: "0.6rem" }}
                      />
                    ))}
                    {emp.tags.length > 2 && (
                      <Chip
                        size="small"
                        label={`+${emp.tags.length - 2}`}
                        variant="outlined"
                        sx={{ height: 18, fontSize: "0.6rem" }}
                      />
                    )}
                  </Stack>
                )}
              </Box>
            ))}
          </Box>
        )}

        <Divider sx={{ my: 2 }} />

        {/* Generate Employees Section */}
        <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
          Generate AI {employees}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Generate AI {employees.toLowerCase()} based on the company profile.
        </Typography>

        {employeeSuccess && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {employeeSuccess}
          </Alert>
        )}

        <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
          <TextField
            label={`Number of ${employees}`}
            value={employeeCount}
            onChange={(e) => setEmployeeCount(e.target.value)}
            type="number"
            size="small"
            sx={{ width: 180 }}
            inputProps={{ min: 1, max: 10 }}
          />
          <LoadingButton
            loading={generatingEmployees}
            variant="contained"
            onClick={async () => {
              await handleGenerateEmployees();
              fetchCompanyEmployees();
            }}
          >
            Generate {employees}
          </LoadingButton>
        </Box>

        <Box sx={{ mt: 2, p: 2, bgcolor: "action.hover", borderRadius: 1 }}>
          <Typography variant="caption" color="text.secondary">
            <strong>Note:</strong> AI {employees.toLowerCase()} are generated
            based on the company name, industry, and description. Make sure
            company details are filled in on the Details tab for better
            results.
          </Typography>
        </Box>
      </>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          height: isEdit ? "85vh" : "auto",
          maxHeight: "85vh",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h6">
          {isEdit ? "Edit Company" : "New Company"}
        </Typography>
        <IconButton onClick={onClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent
        sx={{ display: "flex", flexDirection: "column", overflow: "hidden" }}
      >
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {isEdit ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              flex: 1,
              overflow: "hidden",
            }}
          >
            <Tabs
              value={currentTab}
              onChange={(_, newValue) => setCurrentTab(newValue)}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              sx={{ borderBottom: 1, borderColor: "divider", flexShrink: 0 }}
            >
              <Tab icon={<Business />} label="Details" iconPosition="start" />
              <Tab
                icon={<TuneOutlined />}
                label="Features"
                iconPosition="start"
              />
              <Tab
                icon={<CreditCard />}
                label="Credit Quota"
                iconPosition="start"
              />
              <Tab icon={<Lock />} label="Security" iconPosition="start" />
              <Tab
                icon={<Group />}
                label={employees}
                iconPosition="start"
              />
            </Tabs>

            <TabPanel value={currentTab} index={0}>
              {renderDetailsTab()}
            </TabPanel>
            <TabPanel value={currentTab} index={1}>
              {renderFeaturesTab()}
            </TabPanel>
            <TabPanel value={currentTab} index={2}>
              {renderCreditQuotaTab()}
            </TabPanel>
            <TabPanel value={currentTab} index={3}>
              {renderSecurityTab()}
            </TabPanel>
            <TabPanel value={currentTab} index={4}>
              {renderEmployeesTab()}
            </TabPanel>
          </Box>
        ) : (
          <Box sx={{ flex: 1, overflow: "auto", pt: 1 }}>
            {renderDetailsTab()}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <LoadingButton
          onClick={handleSave}
          variant="contained"
          loading={loading}
        >
          {isEdit ? "Save Changes" : "Create Company"}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
